import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, of, throwError } from 'rxjs';
import { Loader } from '@googlemaps/js-api-loader';

declare global {
  interface Window {
    google: typeof google;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsService {
  private loader: Loader;
  private loadingPromise: Promise<typeof google.maps> | null = null;
  private apiLoaded = false;

  constructor() {
    this.loader = new Loader({
      apiKey: environment.googleMapsApi.key,
      version: 'weekly',
      libraries: ['places']
    });
  }

  /**
   * Loads the Google Maps API dynamically using the official Loader
   * @returns Observable that resolves when the API is loaded with the Maps namespace
   */
  public loadGoogleMapsApi(): Observable<typeof google.maps> {
    // If API is already loaded, return immediately
    if (this.apiLoaded && window.google && window.google.maps) {
      return of(window.google.maps);
    }

    // If API is currently loading, return the existing promise
    if (this.loadingPromise) {
      return this.wrapPromiseInObservable(this.loadingPromise);
    }

    // Start loading the API using importLibrary instead of load()
    const promise = this.loader
      .importLibrary('maps')
      .then(() => {
        this.apiLoaded = true;
        return window.google.maps;
      });
    
    this.loadingPromise = promise;
    return this.wrapPromiseInObservable(promise);
  }

  /**
   * Wraps a promise in an observable
   * @param promise The promise to wrap
   * @returns Observable that resolves with the promise result
   */
  private wrapPromiseInObservable(promise: Promise<typeof google.maps>): Observable<typeof google.maps> {
    if (!promise) {
      return throwError(() => new Error('Promise is null'));
    }

    return new Observable((observer) => {
      promise
        .then((result) => {
          observer.next(result);
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
        });
    });
  }
} 